import {
  SourcingPerformanceCriteria,
  SourcingPerformanceStatus,
} from 'api-clients/monolith';
import _round from 'lodash/round';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import AmberDot from '@/assets/icons/amber-dot.svg';
import BlueDot from '@/assets/icons/blue-dot.svg';
import GreenDot from '@/assets/icons/green-dot.svg';
import GreyDot from '@/assets/icons/grey-dot.svg';
import RedDot from '@/assets/icons/red-dot.svg';

interface UsePerformanceStatusProps {
  status: SourcingPerformanceStatus;
  criteria: SourcingPerformanceCriteria;
  expected: number;
  actual: number;
  startDate?: string;
  endDate?: string;
  duration?: number;
}

export const usePerformanceStatus = ({
  status,
  criteria,
  expected,
  actual,
  startDate,
  endDate,
  duration,
}: UsePerformanceStatusProps) => {
  const intl = useIntl();

  const percentage = Math.abs(_round(((expected - actual) / expected) * 100));
  const trend = expected > actual ? 'lower' : 'higher';
  const type = criteria === 'hire' ? 'hires' : 'applicants';

  const title = useMemo(() => {
    switch (status) {
      case 'goal_met':
        return intl.formatMessage({
          defaultMessage: 'Goal Met',
          description: 'Title shown when the performance goal is met.',
        });
      case 'on_track':
        return intl.formatMessage({
          defaultMessage: 'On Track',
          description: 'Title shown when the performance goal is on track.',
        });
      case 'at_risk':
        return intl.formatMessage({
          defaultMessage: 'At Risk',
          description: 'Title shown when the performance goal is at risk.',
        });
      case 'goal_not_met':
        return intl.formatMessage({
          defaultMessage: 'Goal Not Met',
          description: 'Title shown when the performance goal is not met.',
        });
      case 'not_started':
        return intl.formatMessage({
          defaultMessage: 'Not Started',
          description: 'Title shown when the performance goal is not started.',
        });
      case 'no_data':
        return intl.formatMessage({
          defaultMessage: 'No Data',
          description: 'Title shown when the performance goal is no data.',
        });
      default:
        return intl.formatMessage({
          defaultMessage: 'No Data',
          description: 'Title shown when the performance goal is no data.',
        });
    }
  }, [status, intl]);

  const content: {
    bgColor: string;
    icon: string;
    tooltip: React.ReactNode;
  } = useMemo(() => {
    switch (status) {
      case 'goal_met':
        return {
          bgColor: 'bg-fx-green-50',
          icon: GreenDot,
          tooltip: intl.formatMessage(
            {
              defaultMessage:
                'Hurray! The target was met. There are {actual} {type} out of a goal of {expected}.',
              description:
                'Message shown when the performance goal is met. {actual} is the achieved number, {type} is either "hires" or "applicants", and {expected} is the goal number.',
            },
            {
              type,
              actual,
              expected,
            },
          ),
        };
      case 'on_track':
        return {
          bgColor: 'bg-fx-blue-50',
          icon: BlueDot,
          tooltip:
            expected === actual ? (
              <>
                <FormattedMessage
                  defaultMessage="You are right on track — {actual} {type} so far, matching the expected {expected} {type} by today."
                  description='Message shown when the performance goal is on track. {type} is either "hires" or "applicants", {expected} is the goal number, and {actual} is the achieved number.'
                  values={{ type, expected, actual }}
                />
              </>
            ) : (
              <>
                <FormattedMessage
                  defaultMessage="The number of {type} is {percentage}% {trend} than the expected {type} by today."
                  description='Message shown when the performance goal is on track. {type} is either "hires" or "applicants", {percentage} is the percentage difference, and {trend} is either "lower" or "higher".'
                  values={{ type, percentage, trend }}
                />
                <br />
                <br />
                <FormattedMessage
                  defaultMessage="To hit the goal, there should have been {expected} {type} by today, but there are {actual} {type} already."
                  description='Message shown when the performance goal is on track. {type} is either "hires" or "applicants", {expected} is the goal number, and {actual} is the achieved number.'
                  values={{ type, expected, actual }}
                />
              </>
            ),
        };
      case 'at_risk':
        return {
          bgColor: 'bg-fx-rose-50',
          icon: RedDot,
          tooltip: (
            <>
              <FormattedMessage
                defaultMessage="The number of {type} is {percentage}% {trend} than the expected {type} by today."
                description='Message shown when the performance goal is at risk. {type} is either "hires" or "applicants", {percentage} is the percentage difference, and {trend} is either "lower" or "higher".'
                values={{ type, percentage, trend }}
              />
              <br />
              <br />
              <FormattedMessage
                defaultMessage="To hit the goal, there should have been {expected} {type} by today, but there are only {actual} {type}."
                description='Message shown when the performance goal is at risk. {type} is either "hires" or "applicants", {expected} is the goal number, and {actual} is the achieved number.'
                values={{ type, expected, actual }}
              />
            </>
          ),
        };
      case 'goal_not_met':
        return {
          bgColor: 'bg-amber-50',
          icon: AmberDot,
          tooltip: (
            <>
              <FormattedMessage
                defaultMessage="The hiring goal for this opening ended on {endDate} without reaching the targeted number of {expected} hires."
                values={{ endDate, expected }}
                description="Past-goal state: {endDate} and {expected} are goal end date and target hire count"
              />
              <br />
              <br />
              <FormattedMessage
                defaultMessage="Duration: {startDate} to {endDate} ({duration} days)"
                values={{ startDate, endDate, duration }}
                description="Hiring window line; placeholders are formatted dates and day count"
              />
              <br />
              <br />
            </>
          ),
        };
      case 'not_started':
      case 'no_data':
      default:
        return {
          bgColor: 'bg-fx-neutral-100',
          icon: GreyDot,
          tooltip: intl.formatMessage({
            defaultMessage:
              'There is not enough data available at this time to accurately assess progress',
            description:
              'Message shown when there is not enough data available to accurately assess progress.',
          }),
        };
    }
  }, [
    status,
    intl,
    type,
    actual,
    expected,
    percentage,
    trend,
    endDate,
    startDate,
    duration,
  ]);

  return { ...content, title };
};
